import { ImageServiceType } from '@omni/kit/Types';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import { IGroupEvent } from '@omni/kit/services/GroupsService/Types';
import { getDateRangeParts } from '@omni/kit/utilities/dateFormatRange';
import { createImageUrl, parseImageUrl } from '@omni/kit/utilities/utilities';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';

import { parseRRule } from '../utilities/parseRrule';
import type { TranslationFunction } from '../utilities/repeatEventMessage';
import { generateFrequencyMessage } from '../utilities/repeatEventMessage';

export interface Event extends IGroupEvent {
  image: string | undefined;
  eventSubtitle: string;
  formatedDate: string;
  sectionHeaderDate: string;
}

export interface GroupedEventSection {
  sectionHeader: string;
  data: Event[];
}

export type DayTranslations = {
  today: string;
  tomorrow: string;
};

export const useTransformGroupEvents = (
  events: Event[]
): {
  upcomingEvents: GroupedEventSection[];
  pastEvents: GroupedEventSection[];
} => {
  const [upcomingData, setUpcomingData] = useState<GroupedEventSection[]>([]);
  const [pastData, setPastData] = useState<GroupedEventSection[]>([]);
  const { t } = useTranslation();

  const transformData = useCallback(
    (
      events: Event[]
    ): {
      upcomingEvents: GroupedEventSection[];
      pastEvents: GroupedEventSection[];
    } => {
      const nowUTC = moment.utc();

      // Helper function to check if an event has ended
      const hasEventEnded = (event: Event): boolean => {
        const startAtUTC = moment.utc(event.start_at);
        const endAtUTC = moment.utc(event.end_at);

        if (event.all_day) {
          // For all-day events, check if the start date is before today
          return startAtUTC.isBefore(nowUTC, 'day');
        } else {
          // For timed events, all events must have an end_at time
          if (!event.end_at) {
            return false; // Treat as ongoing if end_at is missing
          }

          return endAtUTC.isBefore(nowUTC);
        }
      };

      // Separate past and upcoming events
      const upcomingEvents = events
        .filter((event) => event.id)
        .filter((event) => moment.utc(event.start_at).isSameOrAfter(nowUTC))
        .filter((event) => event.status === 'published')
        .sort((a, b) =>
          moment.utc(a.start_at).isBefore(moment.utc(b.start_at)) ? -1 : 1
        );

      const pastEvents = events
        .filter((event) => event.id)
        .filter((event) => hasEventEnded(event))
        .filter((event) => event.status === 'published')
        .sort((a, b) =>
          moment.utc(a.start_at).isBefore(moment.utc(b.start_at)) ? 1 : -1
        );

      const groupedUpcomingEvents = getGroupEvents(upcomingEvents, t);
      const groupedPastEvents = getGroupEvents(pastEvents, t);

      return {
        upcomingEvents: groupedUpcomingEvents,
        pastEvents: groupedPastEvents,
      };
    },
    [t]
  );

  useEffect(() => {
    const { upcomingEvents: newUpcomingData, pastEvents: newPastData } =
      transformData(events);

    setUpcomingData((prevData) =>
      JSON.stringify(prevData) !== JSON.stringify(newUpcomingData)
        ? newUpcomingData
        : prevData
    );

    setPastData((prevData) =>
      JSON.stringify(prevData) !== JSON.stringify(newPastData)
        ? newPastData
        : prevData
    );
  }, [events, transformData]);

  return { upcomingEvents: upcomingData, pastEvents: pastData };
};

export const getGroupEvents = (
  eventList: Event[],
  t: TranslationFunction
): GroupedEventSection[] => {
  return eventList.reduce(
    (accumulator: GroupedEventSection[], currentValue) => {
      const { dateString, timeString, allDay, timezone } = getDateRangeParts({
        startDate: currentValue.start_at ? currentValue.start_at : '',
        endDate: currentValue.end_at,
        timezone: currentValue.timezone,
        allDay: currentValue.all_day,
      });

      let formatedDate = dateString;

      if (timeString) {
        formatedDate += ` \u2022 ${timeString}`;
      }

      if (allDay === false && timezone?.isLocal === false) {
        formatedDate += ` ${timezone?.abbreviated}`;
      }

      let eventTimeSubtitle = timeString;

      if (timeString && timezone?.isLocal === false) {
        eventTimeSubtitle += ` ${timezone?.abbreviated}`;
      }

      const imgUrl = parseImageUrl(
        createImageUrl(currentValue?._embedded?.images?.[0].id),
        36,
        36,
        ImageServiceType.ImageJpeg
      );
      const event: Event = {
        ...currentValue,
        image: imgUrl,
        eventSubtitle: eventTimeSubtitle ? eventTimeSubtitle : dateString,
        formatedDate,
        sectionHeaderDate: formatSectionHeader(
          t,
          currentValue.start_at,
          currentValue.timezone
        ),
      };

      if (currentValue._embedded && currentValue._embedded['repeating-event']) {
        const rrule = parseRRule(
          currentValue._embedded['repeating-event']?.repetition_rules?.[1]
        );
        const rruleMess = generateFrequencyMessage(
          rrule?.frequency || '',
          moment.utc(event?.start_at).startOf('day').toDate(),
          rrule?.stopEventDate || null,
          rrule?.weekNumberInMonth || null,
          t
        );
        event.eventSubtitle = eventTimeSubtitle
          ? `${eventTimeSubtitle} • ${rruleMess}`
          : dateString;
      }

      const sectionHeader = accumulator?.findIndex(
        (section) => section.sectionHeader === event.sectionHeaderDate
      );

      if (sectionHeader === -1) {
        accumulator.push({
          sectionHeader: event.sectionHeaderDate,
          data: [event],
        });
      } else {
        accumulator[sectionHeader].data.push(event);
      }

      return accumulator;
    },
    []
  );
};

const formatSectionHeader = (
  t: TranslationFunction,
  startDate?: string,
  timezone?: string
): string => {
  let startInEventTime = moment(startDate);
  let nowInEventTime = moment();

  if (timezone) {
    try {
      startInEventTime = startInEventTime.tz(timezone);
      nowInEventTime = nowInEventTime.tz(timezone);
    } catch (_) {
      // pass
    }
  }

  if (startInEventTime.isSame(nowInEventTime, 'day')) {
    return t('schedule:today');
  } else if (
    startInEventTime.isSame(moment(nowInEventTime).add(1, 'day'), 'day')
  ) {
    return t('schedule:tomorrow');
  } else if (!startInEventTime.isSame(nowInEventTime, 'year')) {
    return startInEventTime.format('dddd, MMMM D, YYYY');
  } else {
    return startInEventTime.format('dddd, MMMM D');
  }
};
